import type {
  DatabaseRelationType,
  FieldType,
  RegistryFieldValueType,
  RegistryType,
} from "@/schemas/digital-files";
import type { Section } from "../types";

const flatFields = (sections: Section[]): FieldType[] =>
  sections.flatMap((s) => s.fields);
import type { ProccesedExpedientValues } from "@/types/entities/templates";
import { getDataCenterDataById } from "@/queries/data-center/data-center.api";

/**
 * Fetches values from data center based on relations
 * @param relations - Array of data center relations
 * @param entityField - The entity field configuration
 * @returns Array of fetched values (or display values as fallback)
 */
const fetchDataCenterValues = async (
  relations: Array<DatabaseRelationType>,
  entityField: FieldType,
): Promise<string[]> => {
  const fetchedValues: string[] = [];

  const tableName = entityField?.configuration?.options_root_table;
  const columnName = entityField?.configuration?.options_root_column;

  for (const relation of relations) {
    try {
      if (!tableName || !columnName) {
        if (relation.display_value) fetchedValues.push(relation.display_value);
        continue;
      }

      const response = await getDataCenterDataById(
        tableName,
        relation.relation_id,
      );

      if (response?.data?.length > 0) {
        fetchedValues.push(response.data[0][columnName]);
      }
    } catch (error) {
      console.error("Error fetching data center value:", error);
      if (relation.display_value) fetchedValues.push(relation.display_value);
    }
  }

  return fetchedValues;
};

function getDisplayValues(
  data: RegistryType,
  entityField: FieldType,
  dataCenterRelations: DatabaseRelationType[],
): Promise<unknown[]> | unknown[] {
  if (dataCenterRelations.length > 0) {
    return fetchDataCenterValues(dataCenterRelations, entityField);
  }

  const fieldType = entityField.field_type?.toLowerCase();
  if (fieldType === "file" || fieldType === "url") {
    const fieldRegistry = data.field_values?.find(
      (el) => el.expedient_base_entity_field_id === entityField.id,
    );
    return fieldRegistry?.files || ["No Asignado"];
  }

  const formattedValue = data.field_values_formatted?.[entityField.name];
  if (
    formattedValue !== null &&
    formattedValue !== undefined &&
    formattedValue !== ""
  ) {
    return [formattedValue];
  }
  return ["No Asignado"];
}

function getRelationsForField(
  data: RegistryType,
  fieldId: string,
  relationType: string,
): DatabaseRelationType[] {
  return (
    data.data_center_relations?.filter(
      (rel) =>
        rel.expedient_base_entity_field_id === fieldId &&
        rel.relation_type === relationType,
    ) || []
  );
}

export const processValues = async (data: RegistryType | undefined) => {
  const processed: ProccesedExpedientValues = {};

  if (!data?.field_values || !Array.isArray(data.field_values))
    return processed;

  for (const fieldValue of data.field_values) {
    const fieldId = fieldValue.expedient_base_entity_field_id;
    const entityField = fieldValue.entity_field;
    if (!fieldId || !entityField) continue;

    const valueRelations = getRelationsForField(data, fieldId, "field_value");
    const optionRelations = getRelationsForField(
      data,
      fieldId,
      "selected_option",
    );

    const displayValues = await getDisplayValues(
      data,
      entityField,
      valueRelations,
    );
    const optionsValues =
      optionRelations.length > 0
        ? await fetchDataCenterValues(optionRelations, entityField)
        : [];

    processed[fieldId] = { value: displayValues, options: optionsValues };
  }

  return processed;
};

/**
 * Return the fields to save excluding file fields
 * key by the field registry id
 * @param sections
 * @param fieldValues
 * @returns
 */
function serializeSelectValue(fieldValue: unknown, field: FieldType): unknown {
  const hasDataCenter =
    field.configuration?.options_root_table &&
    field.configuration?.options_root_column;

  const val: { key: string; label: string; table?: string } =
    typeof fieldValue === "object"
      ? (fieldValue as { key: string; label: string; table?: string })
      : {
          key:
            typeof fieldValue === "string" ||
            typeof fieldValue === "number" ||
            typeof fieldValue === "boolean"
              ? String(fieldValue)
              : "",
          label: "",
          table: field.configuration?.options_root_table || "",
        };

  if (hasDataCenter) {
    return {
      id: val.key,
      table: val.table,
      display: val.label,
      source: "data_center",
    };
  }
  return typeof val === "object" && val.key ? val.key : val;
}

function serializeMultiselectValue(
  fieldValue: unknown,
  field: FieldType,
): unknown {
  const hasDataCenter =
    field.configuration?.options_root_table &&
    field.configuration?.options_root_column;
  const vals = fieldValue as Array<{
    key: string;
    label: string;
    table?: string;
  }>;

  if (!Array.isArray(vals) || vals.length === 0) return undefined;

  if (hasDataCenter) {
    return vals.map((v) => ({
      id: v.key,
      table: field.configuration?.options_root_table || "",
      display: v.label || "",
      source: "data_center",
    }));
  }
  if (typeof vals[0] === "object" && vals[0].key) {
    return vals.map((v) => v.key);
  }
  return vals;
}

function serializeGenericValue(fieldValue: unknown): unknown {
  if (Array.isArray(fieldValue) || typeof fieldValue === "object")
    return fieldValue;
  if (
    typeof fieldValue === "string" ||
    typeof fieldValue === "number" ||
    typeof fieldValue === "boolean"
  ) {
    return String(fieldValue);
  }
  return "";
}

const isNonEmpty = (val: unknown) =>
  val !== undefined && val !== null && val !== "";

export const getFieldsToSave = (
  sections: Section[],
  fieldValues: Record<string, unknown>,
  fieldValuesSource: RegistryFieldValueType[],
): Record<string, unknown> => {
  const result: Record<string, unknown> = {};

  for (const field of flatFields(sections)) {
    const fieldValue = fieldValues[field.id];

    if (field.field_type === "SELECT" && isNonEmpty(fieldValue)) {
      result[field.id] = serializeSelectValue(fieldValue, field);
    } else if (field.field_type === "MULTISELECT" && isNonEmpty(fieldValue)) {
      const serialized = serializeMultiselectValue(fieldValue, field);
      if (serialized !== undefined) result[field.id] = serialized;
    } else if (field.field_type === "URL") {
      const fieldRegistry = fieldValuesSource.some(
        (el) => el.expedient_base_entity_field_id === field.id,
      );
      if (!fieldRegistry) result[field.id] = "";
    } else if (isNonEmpty(fieldValue)) {
      result[field.id] = serializeGenericValue(fieldValue);
    }
  }
  return result;
};

/**
 * Return the file fields to save
 *
 * @param sections
 * @param fieldValues
 * @param fieldValuesSource
 * @returns
 */
export const getFilesToSave = (
  sections: Section[],
  fieldValues: Record<string, unknown>,
  fieldValuesSource: RegistryFieldValueType[],
): Array<{ fieldId: string; value: unknown }> => {
  const fileFields: Array<{ fieldId: string; value: unknown }> = [];

  for (const field of flatFields(sections)) {
    if (field.field_type !== "URL") continue;
    const fieldValue = fieldValues[field.id];
    if (fieldValue === undefined || fieldValue === null) continue;

    const fieldRegistry = fieldValuesSource.find(
      (el) => el.expedient_base_entity_field_id === field.id,
    );
    if (!fieldRegistry) continue;

    const hasNewFiles =
      fieldValue instanceof File ||
      (Array.isArray(fieldValue) && fieldValue.some((f) => f instanceof File));

    if (hasNewFiles) {
      fileFields.push({ fieldId: fieldRegistry.id, value: fieldValue });
    }
  }
  return fileFields;
};

/**
 * Get files that need to be deleted
 * Compares current field values with original registry data
 * For single-file fields: Excludes files that are being replaced (when new File instance is present)
 * For multi-file fields: Deletes removed files even when adding new ones
 * @param sections
 * @param fieldValues - Current field values
 * @param registryData - Original registry data
 * @returns Array of file IDs to delete
 */
function findRemovedFileIds(
  originalFiles: Array<{ id: string }>,
  currentFiles: unknown[],
): string[] {
  return originalFiles
    .filter((original) => {
      const stillExists = currentFiles.some(
        (cf) =>
          typeof cf === "object" &&
          cf !== null &&
          "id" in cf &&
          (cf as { id: string }).id === original.id,
      );
      return !stillExists && original.id;
    })
    .map((f) => f.id);
}

export const getFilesToDelete = (
  sections: Section[],
  fieldValues: Record<string, unknown>,
  registryData?: RegistryType,
): string[] => {
  if (!registryData?.field_values) return [];

  const filesToDelete: string[] = [];

  for (const field of flatFields(sections)) {
    if (field.field_type !== "URL") continue;

    const currentValue = fieldValues[field.id];
    const originalFieldValue = registryData.field_values.find(
      (fv) => fv.expedient_base_entity_field_id === field.id,
    );
    if (!originalFieldValue?.files?.length) continue;

    const allowMultipleFiles = field.configuration?.allowMultipleFiles ?? false;
    const hasNewFile =
      currentValue instanceof File ||
      (Array.isArray(currentValue) &&
        currentValue.some((f) => f instanceof File));
    if (!allowMultipleFiles && hasNewFile) continue;

    const currentFiles = Array.isArray(currentValue) ? currentValue : [];
    filesToDelete.push(
      ...findRemovedFileIds(originalFieldValue.files, currentFiles),
    );
  }

  return filesToDelete;
};

// ===== CHANGE DETECTION HELPERS =====

/**
 * Normalizes any value to a string for consistent comparison.
 * Handles special cases like arrays, objects, null, undefined, etc.
 */
export const normalizeValueForComparison = (val: unknown): string => {
  // Empty values are normalized to empty string
  if (val === null || val === undefined || val === "") return "";

  // Arrays
  if (Array.isArray(val)) {
    if (val.length === 0) return "";

    // Extract relevant values from objects inside the array
    const normalized = val
      .map((v) => {
        if (typeof v === "object" && v !== null && "id" in v) {
          return (v as { id: string }).id;
        }
        if (typeof v === "object" && v !== null && "key" in v) {
          return (v as { key: string }).key;
        }
        return v;
      })
      .sort((a, b) => String(a).localeCompare(String(b)));

    return JSON.stringify(normalized);
  }

  // Objects
  if (typeof val === "object" && val !== null) {
    if ("value" in val) return String((val as { value?: string }).value || "");
    if ("id" in val) return String((val as { id?: string }).id || "");
    return JSON.stringify(val);
  }

  // Primitives (string, number, boolean)
  if (
    typeof val === "string" ||
    typeof val === "number" ||
    typeof val === "boolean"
  ) {
    return String(val).trim();
  }
  return "";
};

/**
 * Gets the default value of a field based on its configuration.
 * Used in creation mode to compare initial values.
 */
export const getFieldDefaultValue = (field: FieldType) => {
  if (field.default_value === null || field.default_value === undefined) {
    return "";
  }

  // Identification field has special structure
  if (field.configuration?.type === "identification") {
    return { value: field.default_value };
  }

  // Multiselect returns array of {key, label} objects
  if (field.field_type === "MULTISELECT") {
    return [
      {
        key: field.default_value,
        label: field.default_value,
      },
    ];
  }

  return field.default_value;
};

/**
 * Finds the initial value of a field in saved data using the field name.
 * Important: We use name instead of ID because IDs can change.
 */
export const findInitialFieldValueByName = (
  fieldName: string,
  fieldType: string,
  registryData: Partial<RegistryType> | null | undefined,
): unknown => {
  if (!registryData?.field_values) return "";

  const initialValueObj = registryData.field_values.find(
    (fv) =>
      (fv as Partial<RegistryFieldValueType>).entity_field?.name === fieldName,
  );

  if (!initialValueObj) return "";

  if (fieldType === "URL") return initialValueObj.files || [];

  if (fieldType === "MULTISELECT") {
    try {
      const parsed =
        typeof initialValueObj.value === "string" && initialValueObj.value
          ? JSON.parse(initialValueObj.value)
          : initialValueObj.value;
      return parsed || [];
    } catch {
      return initialValueObj.value ? [initialValueObj.value] : [];
    }
  }

  return initialValueObj.value ?? "";
};

/**
 * Finds the selected Data Center option for a field.
 * Uses the field name to find the corresponding relation.
 */
export const findInitialDataCenterOptionByName = (
  fieldName: string,
  registryData: Partial<RegistryType> | null | undefined,
): string => {
  if (!registryData?.field_values || !registryData?.data_center_relations) {
    return "";
  }

  const fieldValue = registryData.field_values.find(
    (fv) =>
      (fv as Partial<RegistryFieldValueType>).entity_field?.name === fieldName,
  );
  if (!fieldValue) return "";

  const dataCenterRel = registryData.data_center_relations.find(
    (rel) =>
      (rel as Partial<DatabaseRelationType>).expedient_base_entity_field_id ===
        fieldValue.expedient_base_entity_field_id &&
      (rel as Partial<DatabaseRelationType>).relation_type ===
        "selected_option",
  );

  return (dataCenterRel as Partial<DatabaseRelationType>)?.relation_id || "";
};

/**
 * Compares two arrays of IDs in a sorted manner.
 * Useful for comparing lists of users or related registries.
 */
export const compareIdArrays = (arr1: string[], arr2: string[]): boolean => {
  const sorted1 = [...arr1].sort((a, b) => a.localeCompare(b));
  const sorted2 = [...arr2].sort((a, b) => a.localeCompare(b));
  return JSON.stringify(sorted1) === JSON.stringify(sorted2);
};
